import React, { useState, useEffect, useCallback } from "react";
import FileManager from "./FileManager";
import Lessons from "./Lessons";
import Scratchpad from "./Scratchpad";
import { UploadedFile } from "../../types";

export type SidePaneView = "explorer" | "examples" | "scratchpad";

interface SidePaneProps {
	isCollapsed: boolean;
	onToggleCollapse: () => void;
	sidebarWidth: number;

	// File manager props
	files: Map<string, UploadedFile>;
	onFileUpload: (files: FileList) => void;
	onRemoveFile: (fileName: string) => void;
	onClearAll: () => void;
	onViewFile?: (fileName: string, content: string) => void;

	// Examples props
	onLoadExample?: (code: string, title: string) => void;
	hasWalkthrough?: (filename: string) => boolean;
	onStartWalkthrough?: (filename: string) => void;
}

const SidePane: React.FC<SidePaneProps> = ({
	isCollapsed,
	onToggleCollapse,
	sidebarWidth,
	files,
	onFileUpload,
	onRemoveFile,
	onClearAll,
	onViewFile,
	onLoadExample,
	hasWalkthrough,
	onStartWalkthrough,
}) => {
	const [activeView, setActiveView] = useState<SidePaneView>("explorer");

	// Double-click detection state
	const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null);
	const [clickCount, setClickCount] = useState(0);

	const menuItems = [
		{
			id: "explorer" as SidePaneView,
			icon: (
				<svg
					className="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
					/>
				</svg>
			),
			title: "Explorer",
			badge: files.size > 0 ? files.size : undefined,
		},
		{
			id: "examples" as SidePaneView,
			icon: (
				<svg
					className="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
					/>
				</svg>
			),
			title: "Lessons",
		},
		{
			id: "scratchpad" as SidePaneView,
			icon: (
				<svg
					className="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="2"
						d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
					/>
				</svg>
			),
			title: "Scratchpad",
		},
	];

	// Handle menu item clicks with double-click detection
	const handleMenuItemClick = (item: (typeof menuItems)[0]) => {
		// Clear any existing timeout
		if (clickTimeout) {
			clearTimeout(clickTimeout);
			setClickTimeout(null);
		}

		const newClickCount = clickCount + 1;
		setClickCount(newClickCount);

		if (newClickCount === 1) {
			// First click - set a timeout to handle single click
			const timeout = setTimeout(() => {
				// Handle single click
				if (activeView === item.id && !isCollapsed) {
					// Same button clicked when pane is open - close the pane
					onToggleCollapse();
				} else {
					// Different button or pane is closed - switch view and ensure pane is open
					if (isCollapsed) {
						onToggleCollapse();
					}
					setActiveView(item.id);
				}

				// Reset click count
				setClickCount(0);
				setClickTimeout(null);
			}, 250); // 250ms delay for double-click detection

			setClickTimeout(timeout);
		} else if (newClickCount === 2) {
			// Double click detected - toggle collapsed state
			onToggleCollapse();

			// Reset click count
			setClickCount(0);
			setClickTimeout(null);
		}
	};

	// Reset click count if user clicks elsewhere or after timeout
	const resetClickState = useCallback(() => {
		if (clickTimeout) {
			clearTimeout(clickTimeout);
			setClickTimeout(null);
		}
		setClickCount(0);
	}, [clickTimeout]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (clickTimeout) {
				clearTimeout(clickTimeout);
			}
		};
	}, [clickTimeout]);

	// Reset click state when clicking outside the menu
	useEffect(() => {
		const handleGlobalClick = () => {
			resetClickState();
		};

		document.addEventListener("click", handleGlobalClick);
		return () => document.removeEventListener("click", handleGlobalClick);
	}, [resetClickState]);

	const renderContent = () => {
		switch (activeView) {
			case "explorer":
				return (
					<FileManager
						files={files}
						onFileUpload={onFileUpload}
						onRemoveFile={onRemoveFile}
						onClearAll={onClearAll}
						onViewFile={onViewFile}
						isEmbedded={true}
					/>
				);

			case "examples":
				return (
					<Lessons
						onLoadLesson={onLoadExample || (() => {})}
						isEmbedded={true}
						hasWalkthrough={hasWalkthrough}
						onStartWalkthrough={onStartWalkthrough}
					/>
				);

			case "scratchpad":
				return <Scratchpad isEmbedded={true} />;

			default:
				return null;
		}
	};

	return (
		<div className="flex h-full">
			{/* Menu Icon Rail */}
			<div className="w-12 bg-white/60 dark:bg-[#0c0c0e]/60 backdrop-blur-md border-r border-gray-200/80 dark:border-white/[0.06] flex flex-col">
				<div className="flex-1 flex flex-col items-center py-2.5 gap-0.5">
					{menuItems.map((item) => {
						const isActive = activeView === item.id && !isCollapsed;
						return (
							<div key={item.id} className="relative group">
								<button
									onClick={(e) => {
										e.stopPropagation();
										handleMenuItemClick(item);
									}}
									className={`relative w-9 h-9 flex items-center justify-center rounded-md transition-colors duration-150 ${
										isActive
											? "text-gray-900 dark:text-white bg-gray-100 dark:bg-white/[0.08]"
											: "text-gray-500 dark:text-gray-500 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100/70 dark:hover:bg-white/[0.04]"
									}`}
									title={item.title}
								>
									{/* Active rail indicator */}
									{isActive && (
										<span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-r-full bg-orange-500 dark:bg-amber-400" />
									)}
									{item.icon}

									{item.badge && (
										<div className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] bg-orange-500 text-white text-[9px] tabular rounded-full flex items-center justify-center px-1 font-medium ring-2 ring-white dark:ring-[#0c0c0e]">
											{item.badge}
										</div>
									)}
								</button>

								<div className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-2 py-1 rounded-md text-[11px] font-medium tracking-tight whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none shadow-lg z-50">
									{item.title}
								</div>
							</div>
						);
					})}
				</div>
			</div>

			{/* Content Area */}
			<div
				className={`h-full transition-all duration-300 ${
					isCollapsed
						? "w-0 overflow-hidden"
						: "bg-white/95 dark:bg-[#0c0c0e]/95 border-r border-gray-200/80 dark:border-white/[0.06]"
				}`}
				style={{
					width: isCollapsed ? 0 : `${sidebarWidth}px`,
				}}
			>
				{renderContent()}
			</div>
		</div>
	);
};

export default SidePane;
